import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { 
  Users, MapPin, Calendar, Plus, X, RefreshCw, Clock, CheckCircle2, 
  AlertCircle, Link2, Trash2, ChevronDown, ChevronRight, Timer,
  UserPlus, Layers, Target, Wrench, Zap, Settings, Play, Square,
  User, UserCheck, Filter, Search, MoreVertical, Award,
  CheckSquare, ListChecks, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

// Skill definitions
const SKILLS = {
  dtm: { label: "DTM", color: "bg-blue-500", description: "DT Metering (All can do)" },
  dtc: { label: "DTC", color: "bg-purple-500", description: "DT Commissioning (Skilled)" },
  mi: { label: "MI", color: "bg-green-500", description: "Meter Installation (Skilled)" }
};

const STATUS_CONFIG = {
  new: { bg: "bg-slate-100", text: "text-slate-700", label: "New" },
  ci_done: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Survey Done" },
  rci_done: { bg: "bg-amber-100", text: "text-amber-700", label: "RCI Done" },
  dtm_done: { bg: "bg-blue-100", text: "text-blue-700", label: "DTM Done" },
  dtc_done: { bg: "bg-purple-100", text: "text-purple-700", label: "DTC Done" },
  mi_done: { bg: "bg-green-100", text: "text-green-700", label: "MI Done" },
  qc_passed: { bg: "bg-emerald-100", text: "text-emerald-700", label: "QC Passed" },
  paid: { bg: "bg-teal-100", text: "text-teal-700", label: "Paid" },
};

const AVAILABLE_TASKS = [
  { key: "survey", label: "Survey" },
  { key: "dtm", label: "DTM" },
  { key: "dtc", label: "DTC" },
  { key: "mi", label: "MI" }
];

export default function FieldOperationsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [technicians, setTechnicians] = useState([]);
  const [helpers, setHelpers] = useState([]);
  const [feeders, setFeeders] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [taskForces, setTaskForces] = useState([]);
  
  // DT-level states
  const [feederDTs, setFeederDTs] = useState([]);
  const [feederDTsLoading, setFeederDTsLoading] = useState(false);
  const [selectedDTs, setSelectedDTs] = useState(new Set());
  const [dtStatusFilter, setDtStatusFilter] = useState("all");
  
  // UI states
  const [selectedFeeder, setSelectedFeeder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSkillModal, setShowSkillModal] = useState(null);
  const [showPairModal, setShowPairModal] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [expandedTech, setExpandedTech] = useState(null);
  const [activeView, setActiveView] = useState("feeders"); // feeders | dts

  const fetchAllData = async () => {
    setRefreshing(true);
    try {
      const [techRes, helperRes, feederRes, assignRes, pairRes, rosterRes] = await Promise.all([
        api.get("/assignments/available-technicians"),
        api.get("/assignments/available-helpers"),
        api.get("/assignments/feeders-for-assignment"),
        api.get("/assignments?status=active"),
        api.get("/assignments/pairs"),
        api.get("/rosters/my-roster")
      ]);
      
      setTechnicians(techRes.data.technicians || []);
      setHelpers(helperRes.data.helpers || []);
      setFeeders(feederRes.data.feeders || []);
      setAssignments(assignRes.data.items || []);
      setPairs(pairRes.data.pairs || []);
      setTaskForces(rosterRes.data.roster ? [rosterRes.data.roster] : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchAllData(); }, []);

  // Fetch DTs when feeder is selected
  const fetchFeederDTs = async (feederCode) => {
    setFeederDTsLoading(true);
    setSelectedDTs(new Set());
    setDtStatusFilter("all");
    try {
      const res = await api.get(`/assignments/dts-for-feeder/${feederCode}`);
      setFeederDTs(res.data.dts || []);
    } catch (e) {
      toast.error("Failed to load DTs");
      setFeederDTs([]);
    } finally {
      setFeederDTsLoading(false);
    }
  };

  const handleFeederSelect = (feederCode) => {
    if (selectedFeeder === feederCode) {
      setSelectedFeeder(null);
      setActiveView("feeders");
      setFeederDTs([]);
      setSelectedDTs(new Set());
    } else {
      setSelectedFeeder(feederCode);
      setActiveView("dts");
      fetchFeederDTs(feederCode);
    }
  };

  // Compute enriched data
  const enrichedTechnicians = useMemo(() => {
    return technicians.map(tech => {
      const techAssignments = assignments.filter(a => a.technician_id === tech.id);
      const pair = pairs.find(p => p.technician_id === tech.id);
      const skills = tech.skills || ["dtm"];
      return {
        ...tech, assignments: techAssignments, pair,
        helper: pair ? helpers.find(h => h.id === pair.helper_id) : null,
        skills, feeders: techAssignments.filter(a => a.feeder_code).map(a => a.feeder_code)
      };
    });
  }, [technicians, assignments, pairs, helpers]);

  const enrichedFeeders = useMemo(() => {
    return feeders.map(feeder => {
      const feederAssignments = assignments.filter(a => a.feeder_code === feeder.feeder_code);
      const assignedTechs = feederAssignments.map(a => {
        const tech = technicians.find(t => t.id === a.technician_id);
        return tech ? { ...tech, assignment: a } : null;
      }).filter(Boolean);
      return { ...feeder, assignedTechs, isAssigned: assignedTechs.length > 0 };
    });
  }, [feeders, assignments, technicians]);

  const filteredFeeders = useMemo(() => {
    if (!searchTerm) return enrichedFeeders;
    const term = searchTerm.toLowerCase();
    return enrichedFeeders.filter(f => 
      f.feeder_code?.toLowerCase().includes(term) ||
      f.feeder_name?.toLowerCase().includes(term)
    );
  }, [enrichedFeeders, searchTerm]);

  // Filter DTs by status
  const filteredDTs = useMemo(() => {
    if (dtStatusFilter === "all") return feederDTs;
    return feederDTs.filter(dt => dt.lifecycle_status === dtStatusFilter);
  }, [feederDTs, dtStatusFilter]);

  // DT status counts
  const dtStatusCounts = useMemo(() => {
    const counts = {};
    feederDTs.forEach(dt => {
      const s = dt.lifecycle_status || "unknown";
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [feederDTs]);

  // DT selection handlers
  const toggleDTSelection = (dtId) => {
    setSelectedDTs(prev => {
      const next = new Set(prev);
      if (next.has(dtId)) next.delete(dtId); else next.add(dtId);
      return next;
    });
  };

  const selectAllVisible = () => {
    const ids = filteredDTs.map(dt => dt.id);
    setSelectedDTs(new Set(ids));
  };

  const selectUnassigned = () => {
    const ids = filteredDTs.filter(dt => !dt.assigned_to).map(dt => dt.id);
    setSelectedDTs(new Set(ids));
  };

  const clearSelection = () => setSelectedDTs(new Set());

  // Handlers
  const handleAssignFeeder = async (feederCode, technicianId, durationDays = 7) => {
    try {
      await api.post("/assignments", {
        technician_id: technicianId,
        feeder_code: feederCode,
        duration_type: "days",
        duration_days: durationDays
      });
      toast.success("Feeder assigned!");
      setShowAssignModal(null);
      fetchAllData();
      if (selectedFeeder === feederCode) fetchFeederDTs(feederCode);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign");
    }
  };

  const handleBulkAssign = async (technicianId, durationDays = 7, allowedTasks = null) => {
    try {
      await api.post("/assignments/bulk-assign", {
        technician_id: technicianId,
        dt_ids: Array.from(selectedDTs),
        duration_type: "days",
        duration_days: durationDays,
        allowed_tasks: allowedTasks
      });
      toast.success(`${selectedDTs.size} DTs assigned!`);
      setShowBulkAssignModal(false);
      setSelectedDTs(new Set());
      fetchAllData();
      if (selectedFeeder) fetchFeederDTs(selectedFeeder);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign DTs");
    }
  };

  const handleRevokeAssignment = async (assignmentId) => {
    if (!window.confirm("Remove this assignment?")) return;
    try {
      await api.delete(`/assignments/${assignmentId}`);
      toast.success("Assignment removed");
      fetchAllData();
      if (selectedFeeder) fetchFeederDTs(selectedFeeder);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const handleCreatePair = async (techId, helperId) => {
    try {
      await api.post("/assignments/pairs", { technician_id: techId, helper_id: helperId });
      toast.success("Pair created!");
      setShowPairModal(null);
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create pair");
    }
  };

  const handleBreakPair = async (pairId) => {
    if (!window.confirm("Break this pair?")) return;
    try {
      await api.delete(`/assignments/pairs/${pairId}`);
      toast.success("Pair broken");
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to break pair");
    }
  };

  const handleUpdateSkills = async (techId, skills) => {
    try {
      await api.put(`/employees/${techId}`, { skills });
      toast.success("Skills updated!");
      setShowSkillModal(null);
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update skills");
    }
  };

  // Stats
  const stats = useMemo(() => ({
    totalFeeders: feeders.length,
    assignedFeeders: enrichedFeeders.filter(f => f.isAssigned).length,
    totalTechs: technicians.length,
    pairedTechs: pairs.length,
    pendingDTM: feeders.reduce((sum, f) => sum + (f.pending_dts || 0), 0)
  }), [feeders, enrichedFeeders, technicians, pairs]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-96">
        <RefreshCw size={32} className="animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 animate-fade-up min-h-screen bg-slate-50" data-testid="field-ops-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>
            Field Operations Command
          </h1>
          <p className="text-slate-500 text-sm">Assign feeders & DTs, manage pairs, and track daily work</p>
        </div>
        <button onClick={fetchAllData} disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
          <RefreshCw size={16} className={refreshing ? "animate-spin text-orange-500" : "text-slate-500"} />
          <span className="text-sm font-medium">Refresh</span>
        </button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="text-2xl font-black text-blue-600">{stats.assignedFeeders}/{stats.totalFeeders}</div>
          <div className="text-xs text-slate-500">Feeders Assigned</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="text-2xl font-black text-green-600">{stats.pairedTechs}/{stats.totalTechs}</div>
          <div className="text-xs text-slate-500">Techs Paired</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="text-2xl font-black text-purple-600">{pairs.length}</div>
          <div className="text-xs text-slate-500">Active Pairs</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="text-2xl font-black text-orange-600">{stats.pendingDTM}</div>
          <div className="text-xs text-slate-500">Pending DTs</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="text-2xl font-black text-slate-600">{taskForces.length}</div>
          <div className="text-xs text-slate-500">Today's Tasks</div>
        </div>
      </div>

      {/* Main Content - 3 Panel Layout */}
      <div className="grid lg:grid-cols-12 gap-4">
        
        {/* LEFT PANEL: Feeders */}
        <div className="lg:col-span-4 bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers size={18} />
                <span className="font-bold">Feeders</span>
              </div>
              <span className="text-xs bg-white/20 px-2 py-1 rounded">{filteredFeeders.length}</span>
            </div>
            <div className="mt-2 relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-blue-200" />
              <input type="text" placeholder="Search feeders..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-white/10 border border-white/20 rounded text-sm placeholder-blue-200 focus:outline-none focus:bg-white/20"
                data-testid="feeder-search" />
            </div>
          </div>
          
          <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
            {filteredFeeders.map(feeder => (
              <div key={feeder.feeder_code}
                className={`p-3 hover:bg-slate-50 cursor-pointer transition ${
                  selectedFeeder === feeder.feeder_code ? "bg-blue-50 border-l-4 border-blue-500" : ""
                }`}
                onClick={() => handleFeederSelect(feeder.feeder_code)}
                data-testid={`feeder-item-${feeder.feeder_code}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${feeder.isAssigned ? "bg-green-500" : "bg-slate-300"}`} />
                      <span className="font-semibold text-sm text-slate-800">{feeder.feeder_code}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{feeder.feeder_name}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                        {feeder.pending_dts || 0} pending
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                        {feeder.total_dts || 0} total
                      </span>
                    </div>
                    {feeder.assignedTechs?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {feeder.assignedTechs.map(tech => (
                          <span key={tech.id} className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                            <User size={10} />
                            {tech.full_name?.split(" ")[0]}
                            <button onClick={(e) => { e.stopPropagation(); handleRevokeAssignment(tech.assignment.id); }}
                              className="hover:text-red-600"><X size={10} /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); setShowAssignModal(feeder.feeder_code); }}
                    className="p-1.5 text-blue-600 hover:bg-blue-100 rounded" title="Assign Technician"
                    data-testid={`assign-feeder-btn-${feeder.feeder_code}`}>
                    <UserPlus size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CENTER PANEL: Team Board or DT Detail */}
        {activeView === "dts" && selectedFeeder ? (
          /* DT DETAIL PANEL */
          <div className="lg:col-span-5 bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="dt-detail-panel">
            <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target size={18} />
                  <span className="font-bold">{selectedFeeder} — DTs</span>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded">{feederDTs.length}</span>
                </div>
                <button onClick={() => { setActiveView("feeders"); setSelectedFeeder(null); setFeederDTs([]); setSelectedDTs(new Set()); }}
                  className="p-1 bg-white/20 rounded hover:bg-white/30"><X size={16} /></button>
              </div>

              {/* Status filter pills */}
              <div className="flex flex-wrap gap-1 mt-2">
                <button onClick={() => setDtStatusFilter("all")}
                  className={`px-2 py-0.5 rounded text-[10px] font-bold transition ${dtStatusFilter === "all" ? "bg-white text-orange-600" : "bg-white/20 text-white"}`}
                  data-testid="dt-filter-all">
                  All ({feederDTs.length})
                </button>
                {Object.entries(dtStatusCounts).map(([status, count]) => {
                  const cfg = STATUS_CONFIG[status] || { label: status };
                  return (
                    <button key={status} onClick={() => setDtStatusFilter(status)}
                      className={`px-2 py-0.5 rounded text-[10px] font-bold transition ${dtStatusFilter === status ? "bg-white text-orange-600" : "bg-white/20 text-white"}`}
                      data-testid={`dt-filter-${status}`}>
                      {cfg.label} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Bulk action bar */}
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <button onClick={selectAllVisible} className="px-2 py-1 text-[10px] font-bold bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  data-testid="select-all-dts-btn">
                  <CheckSquare size={12} className="inline mr-1" />Select All ({filteredDTs.length})
                </button>
                <button onClick={selectUnassigned} className="px-2 py-1 text-[10px] font-bold bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                  data-testid="select-unassigned-dts-btn">
                  Unassigned Only
                </button>
                {selectedDTs.size > 0 && (
                  <button onClick={clearSelection} className="px-2 py-1 text-[10px] font-bold bg-slate-200 text-slate-600 rounded hover:bg-slate-300">
                    Clear
                  </button>
                )}
              </div>
              {selectedDTs.size > 0 && (
                <button onClick={() => setShowBulkAssignModal(true)}
                  className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-orange-600 shadow-sm"
                  data-testid="bulk-assign-btn">
                  <ArrowRight size={14} />
                  Assign {selectedDTs.size} DTs
                </button>
              )}
            </div>

            {/* DT List */}
            <div className="divide-y divide-slate-100 max-h-[480px] overflow-y-auto">
              {feederDTsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw size={24} className="animate-spin text-orange-500" />
                </div>
              ) : filteredDTs.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Target size={32} className="mx-auto mb-2" />
                  <div className="text-sm">No DTs found</div>
                </div>
              ) : filteredDTs.map(dt => {
                const statusCfg = STATUS_CONFIG[dt.lifecycle_status] || { bg: "bg-slate-100", text: "text-slate-600", label: dt.lifecycle_status };
                const isSelected = selectedDTs.has(dt.id);
                return (
                  <div key={dt.id}
                    className={`px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 cursor-pointer transition ${isSelected ? "bg-orange-50" : ""}`}
                    onClick={() => toggleDTSelection(dt.id)}
                    data-testid={`dt-item-${dt.dt_code}`}>
                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
                      isSelected ? "bg-orange-500 border-orange-500" : "border-slate-300"
                    }`}>
                      {isSelected && <CheckCircle2 size={14} className="text-white" />}
                    </div>
                    {/* DT Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-800 truncate">{dt.dt_code}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${statusCfg.bg} ${statusCfg.text}`}>
                          {statusCfg.label}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {dt.dt_name || dt.location_text || "—"}
                        {dt.kva_rating ? ` • ${dt.kva_rating} kVA` : ""}
                      </div>
                    </div>
                    {/* Assignment Info */}
                    <div className="text-right flex-shrink-0">
                      {dt.assigned_to ? (
                        <div>
                          <span className="text-[10px] font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                            {dt.assigned_to?.split(" ")[0]}
                          </span>
                          {dt.assigned_via === "feeder" && (
                            <div className="text-[9px] text-slate-400 mt-0.5">via feeder</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] font-medium text-slate-400">Unassigned</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* TEAM BOARD PANEL */
          <div className="lg:col-span-5 bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="team-board-panel">
            <div className="px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white">
              <div className="flex items-center gap-2">
                <Users size={18} />
                <span className="font-bold">Team Board</span>
                <span className="text-xs bg-white/20 px-2 py-1 rounded ml-auto">{enrichedTechnicians.length} Technicians</span>
              </div>
            </div>
            <div className="p-3 space-y-2 max-h-[600px] overflow-y-auto">
              {enrichedTechnicians.map(tech => (
                <div key={tech.id}
                  className={`border rounded-lg overflow-hidden transition ${expandedTech === tech.id ? "border-green-300 shadow-md" : "border-slate-200"}`}>
                  <div className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpandedTech(expandedTech === tech.id ? null : tech.id)}>
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                      {tech.full_name?.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">{tech.full_name}</span>
                        {tech.pair && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded flex items-center gap-1">
                            <Link2 size={10} />Paired
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        {(tech.skills || ["dtm"]).map(skill => (
                          <span key={skill} className={`text-[10px] px-1.5 py-0.5 text-white rounded ${SKILLS[skill]?.color || "bg-slate-500"}`}>
                            {SKILLS[skill]?.label || skill}
                          </span>
                        ))}
                        <button onClick={(e) => { e.stopPropagation(); setShowSkillModal(tech.id); }}
                          className="p-0.5 text-slate-400 hover:text-slate-600">
                          <Settings size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-green-600">{tech.assignments?.length || 0}</div>
                      <div className="text-[10px] text-slate-500">Feeders</div>
                    </div>
                    <ChevronRight size={16} className={`text-slate-400 transition ${expandedTech === tech.id ? "rotate-90" : ""}`} />
                  </div>
                  {expandedTech === tech.id && (
                    <div className="px-3 pb-3 border-t border-slate-100 bg-slate-50">
                      <div className="py-2">
                        <div className="text-xs font-bold text-slate-500 uppercase mb-1">Paired Helper</div>
                        {tech.pair ? (
                          <div className="flex items-center gap-2 p-2 bg-purple-50 rounded">
                            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold">
                              {tech.helper?.full_name?.charAt(0) || "H"}
                            </div>
                            <span className="text-sm font-medium">{tech.helper?.full_name || "Unknown"}</span>
                            <button onClick={() => handleBreakPair(tech.pair.id)}
                              className="ml-auto p-1 text-red-500 hover:bg-red-100 rounded"><X size={14} /></button>
                          </div>
                        ) : (
                          <button onClick={() => setShowPairModal(tech.id)}
                            className="w-full p-2 border-2 border-dashed border-purple-300 rounded text-purple-600 text-sm font-medium hover:bg-purple-50 flex items-center justify-center gap-2">
                            <UserPlus size={14} />Add Helper
                          </button>
                        )}
                      </div>
                      <div className="py-2">
                        <div className="text-xs font-bold text-slate-500 uppercase mb-1">Assigned Feeders</div>
                        {tech.assignments?.length > 0 ? (
                          <div className="space-y-1">
                            {tech.assignments.map(a => (
                              <div key={a.id} className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
                                <div>
                                  <div className="text-sm font-medium">{a.feeder_code}</div>
                                  <div className="text-[10px] text-slate-500">
                                    {a.dt_count || a.dt_ids?.length || 0} DTs
                                    {a.assignment_type === "dt_only" && <span className="ml-1 text-orange-600 font-semibold">(DT-specific)</span>}
                                    {" • "}{a.duration_type === "days" ? `${a.duration_days}d` : a.duration_type}
                                  </div>
                                </div>
                                <button onClick={() => handleRevokeAssignment(a.id)}
                                  className="p-1 text-red-500 hover:bg-red-100 rounded"><Trash2 size={14} /></button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-2 text-center text-slate-400 text-sm">No feeders assigned</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RIGHT PANEL: Today's Work */}
        <div className="lg:col-span-3 bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
            <div className="flex items-center gap-2">
              <Calendar size={18} />
              <span className="font-bold">Today's Work</span>
            </div>
            <div className="text-xs text-orange-100 mt-1">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</div>
          </div>
          <div className="p-3 space-y-3 max-h-[600px] overflow-y-auto">
            {taskForces.length > 0 ? (
              taskForces.map((tf, i) => (
                <div key={i} className="border border-green-200 rounded-lg p-3 bg-green-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-green-700 uppercase">Active</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${tf.status === "active" ? "bg-green-500 text-white" : "bg-slate-200"}`}>
                      {tf.status}
                    </span>
                  </div>
                  <div className="font-semibold text-slate-800">{tf.feeder_code}</div>
                  <div className="text-xs text-slate-500 mt-1">{tf.members?.length || 0} members</div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Calendar size={32} className="mx-auto text-slate-300 mb-2" />
                <div className="text-sm text-slate-500">No active task force</div>
              </div>
            )}
            
            {/* Quick Start */}
            <div className="border-t border-slate-200 pt-3">
              <div className="text-xs font-bold text-slate-500 uppercase mb-2">Quick Start</div>
              {enrichedFeeders.filter(f => f.isAssigned).slice(0, 3).map(feeder => (
                <button key={feeder.feeder_code}
                  onClick={() => handleFeederSelect(feeder.feeder_code)}
                  className="w-full mb-2 p-2 border border-slate-200 rounded flex items-center justify-between hover:bg-orange-50 hover:border-orange-300 transition">
                  <div className="text-left">
                    <div className="text-sm font-medium text-slate-700">{feeder.feeder_code}</div>
                    <div className="text-[10px] text-slate-500">{feeder.assignedTechs?.map(t => t.full_name?.split(" ")[0]).join(", ")}</div>
                  </div>
                  <ChevronRight size={16} className="text-orange-500" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL: Assign Feeder to Technician */}
      {showAssignModal && ReactDOM.createPortal(
        <AssignFeederModal
          feederCode={showAssignModal}
          technicians={enrichedTechnicians}
          onAssign={handleAssignFeeder}
          onClose={() => setShowAssignModal(null)}
        />, document.body
      )}

      {/* MODAL: Bulk Assign DTs */}
      {showBulkAssignModal && ReactDOM.createPortal(
        <BulkAssignModal
          selectedCount={selectedDTs.size}
          technicians={enrichedTechnicians}
          onAssign={handleBulkAssign}
          onClose={() => setShowBulkAssignModal(false)}
        />, document.body
      )}

      {/* MODAL: Pair Helper */}
      {showPairModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg p-5 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Add Helper</h3>
              <button onClick={() => setShowPairModal(null)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {helpers.filter(h => !h.is_paired).length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users size={32} className="mx-auto text-slate-300 mb-2" />No available helpers
                </div>
              ) : helpers.filter(h => !h.is_paired).map(helper => (
                <button key={helper.id} onClick={() => handleCreatePair(showPairModal, helper.id)}
                  className="w-full p-3 border border-slate-200 rounded-lg flex items-center gap-3 hover:bg-purple-50 hover:border-purple-300 transition text-left">
                  <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold">
                    {helper.full_name?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">{helper.full_name}</div>
                    <div className="text-xs text-slate-500">Available</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>, document.body
      )}

      {/* MODAL: Edit Skills */}
      {showSkillModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg p-5 w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Edit Skills</h3>
              <button onClick={() => setShowSkillModal(null)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            <SkillEditor
              techId={showSkillModal}
              currentSkills={technicians.find(t => t.id === showSkillModal)?.skills || ["dtm"]}
              onSave={(skills) => handleUpdateSkills(showSkillModal, skills)}
              onCancel={() => setShowSkillModal(null)}
            />
          </div>
        </div>, document.body
      )}
    </div>
  );
}

// ============ Sub-Components ============

function AssignFeederModal({ feederCode, technicians, onAssign, onClose }) {
  const [durationDays, setDurationDays] = useState(7);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md shadow-xl overflow-hidden">
        <div className="px-5 py-3 bg-blue-600 text-white flex items-center justify-between">
          <div>
            <h3 className="font-bold">Assign {feederCode}</h3>
            <div className="text-xs text-blue-200">Assign entire feeder to a technician</div>
          </div>
          <button onClick={onClose} className="p-1 bg-white/20 rounded hover:bg-white/30"><X size={16} /></button>
        </div>
        <div className="px-5 py-3 bg-slate-50 border-b flex items-center gap-3">
          <span className="text-xs text-slate-500">Duration:</span>
          {[3, 7, 14, 30].map(d => (
            <button key={d} onClick={() => setDurationDays(d)}
              className={`px-2 py-1 rounded text-xs font-bold transition ${durationDays === d ? "bg-blue-500 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
              {d}d
            </button>
          ))}
        </div>
        <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
          {technicians.map(tech => (
            <button key={tech.id} onClick={() => onAssign(feederCode, tech.id, durationDays)}
              className="w-full p-3 border border-slate-200 rounded-lg flex items-center gap-3 hover:bg-green-50 hover:border-green-300 transition text-left"
              data-testid={`assign-tech-${tech.id}`}>
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                {tech.full_name?.charAt(0)}
              </div>
              <div className="flex-1">
                <div className="font-semibold">{tech.full_name}</div>
                <div className="flex items-center gap-1 mt-0.5">
                  {(tech.skills || ["dtm"]).map(skill => (
                    <span key={skill} className={`text-[10px] px-1.5 py-0.5 text-white rounded ${SKILLS[skill]?.color}`}>
                      {SKILLS[skill]?.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-xs text-slate-500">{tech.assignments?.length || 0} feeders</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function BulkAssignModal({ selectedCount, technicians, onAssign, onClose }) {
  const [durationDays, setDurationDays] = useState(7);
  const [selectedTasks, setSelectedTasks] = useState(["survey", "dtm", "dtc", "mi"]);

  const toggleTask = (task) => {
    setSelectedTasks(prev => prev.includes(task) ? prev.filter(t => t !== task) : [...prev, task]);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md shadow-xl overflow-hidden" data-testid="bulk-assign-modal">
        <div className="px-5 py-3 bg-orange-500 text-white flex items-center justify-between">
          <div>
            <h3 className="font-bold">Bulk Assign {selectedCount} DTs</h3>
            <div className="text-xs text-orange-200">Select technician and configure assignment</div>
          </div>
          <button onClick={onClose} className="p-1 bg-white/20 rounded hover:bg-white/30"><X size={16} /></button>
        </div>

        {/* Duration */}
        <div className="px-5 py-3 bg-slate-50 border-b">
          <div className="text-xs font-bold text-slate-500 uppercase mb-2">Duration</div>
          <div className="flex gap-2">
            {[3, 7, 14, 30].map(d => (
              <button key={d} onClick={() => setDurationDays(d)}
                className={`px-3 py-1.5 rounded text-xs font-bold transition ${durationDays === d ? "bg-orange-500 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
                {d} days
              </button>
            ))}
          </div>
        </div>

        {/* Allowed Tasks */}
        <div className="px-5 py-3 bg-slate-50 border-b">
          <div className="text-xs font-bold text-slate-500 uppercase mb-2">Allowed Tasks</div>
          <div className="flex gap-2">
            {AVAILABLE_TASKS.map(task => (
              <button key={task.key} onClick={() => toggleTask(task.key)}
                className={`px-3 py-1.5 rounded text-xs font-bold transition ${selectedTasks.includes(task.key) ? "bg-green-500 text-white" : "bg-white border border-slate-200 text-slate-400"}`}
                data-testid={`bulk-task-${task.key}`}>
                {task.label}
              </button>
            ))}
          </div>
        </div>

        {/* Technician List */}
        <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
          <div className="text-xs font-bold text-slate-500 uppercase mb-1">Select Technician</div>
          {technicians.map(tech => (
            <button key={tech.id}
              onClick={() => onAssign(tech.id, durationDays, selectedTasks.length > 0 ? selectedTasks : null)}
              className="w-full p-3 border border-slate-200 rounded-lg flex items-center gap-3 hover:bg-orange-50 hover:border-orange-300 transition text-left"
              data-testid={`bulk-assign-tech-${tech.id}`}>
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">
                {tech.full_name?.charAt(0)}
              </div>
              <div className="flex-1">
                <div className="font-semibold">{tech.full_name}</div>
                <div className="flex items-center gap-1 mt-0.5">
                  {(tech.skills || ["dtm"]).map(skill => (
                    <span key={skill} className={`text-[10px] px-1.5 py-0.5 text-white rounded ${SKILLS[skill]?.color}`}>
                      {SKILLS[skill]?.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-xs text-slate-500">{tech.assignments?.length || 0} active</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SkillEditor({ techId, currentSkills, onSave, onCancel }) {
  const [skills, setSkills] = useState(currentSkills);
  
  const toggleSkill = (skill) => {
    if (skill === "dtm") return;
    if (skills.includes(skill)) setSkills(skills.filter(s => s !== skill));
    else setSkills([...skills, skill]);
  };
  
  return (
    <div>
      <p className="text-sm text-slate-500 mb-3">Select skills this technician can perform:</p>
      <div className="space-y-2">
        {Object.entries(SKILLS).map(([key, skill]) => (
          <label key={key}
            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition ${
              skills.includes(key) ? "border-green-500 bg-green-50" : "border-slate-200 hover:bg-slate-50"
            } ${key === "dtm" ? "opacity-75" : ""}`}>
            <input type="checkbox" checked={skills.includes(key)} onChange={() => toggleSkill(key)}
              disabled={key === "dtm"} className="w-4 h-4 text-green-500" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-xs text-white rounded ${skill.color}`}>{skill.label}</span>
                {key === "dtm" && <span className="text-[10px] text-slate-400">(Required)</span>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{skill.description}</div>
            </div>
          </label>
        ))}
      </div>
      <div className="flex gap-3 mt-4">
        <button onClick={onCancel} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium">Cancel</button>
        <button onClick={() => onSave(skills)} className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-bold hover:bg-green-600">Save Skills</button>
      </div>
    </div>
  );
}
